import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LiliButton } from '@lili/lili-frontend-common';
import { useStore } from '../../../../store';
import { useMoveFromEmergencyBucketQuery } from '../../../../hooks/query/useMoveFromEmergencyBucketQuery';
import { MONEY_MOVEMENT, SET_UP_AUTO_SAVING } from '../../constants';
import {
  convertIntToFloatStr,
  currencyInputValueToNumber,
  formatAmount,
  handleInputWithCurrencyOnChange,
} from '../../../../helpers/currency.helper';
import { CURRENCY, CURRENCY_USD_SIGN, ENTER_KEY, FEATURE_FLAG, FORMAT_TYPE } from '../../../../constants/general';
import InputWithCurrency from '../../../../generic-components/InputWithCurrency';
import { validateFeatureFlag } from '../../../../services/featureFlagService';
import { trackMixPanel } from '../../../../services/mixpanel.service';
import { useActivateSavingBucket } from '../../../../pages/SavingsBucket/Hooks/useActivateSavingBucket';

interface IProps {
  pullFromBucket: boolean;
}

const EmergencyBucketMoneyMovement = ({ pullFromBucket }: IProps) => {
  const navigate = useNavigate();
  const navigateWIthParams = (navParam: boolean, sum: string) => {
    navigate(`/emergency-bucket-dashboard/:${navParam}-${sum}`);
  };
  const { TITLE, TEXT } = SET_UP_AUTO_SAVING;
  const { ADD, WITHDRAW, INPUT_CLASS_NAME, ERROR_MESSAGE_CLASS_NAME } = MONEY_MOVEMENT;
  const { customerId } = useStore((state) => state.currUser());
  const summary = useStore((state) => state.summary);
  const { getPrimaryAccount } = useStore((state) => state);

  const CHECKING_BALANCE = formatAmount(
    convertIntToFloatStr(getPrimaryAccount().balance),
    FORMAT_TYPE.FORMATTED,
    CURRENCY.USD.SYMBOL,
    false,
  );
  const EMERGENCY_BUCKET_BALANCE = formatAmount(
    convertIntToFloatStr(summary.emergencyBucketResponse.balance),
    FORMAT_TYPE.FORMATTED,
    CURRENCY.USD.SYMBOL,
    false,
  );
  const INITIAL_INPUT_VALUE = '';
  const NULL_INPUT_VALUE = '0';
  const NULL_DOT_INPUT_VALUE = '0.';
  const [inputValue, setInputValue] = useState<string>(INITIAL_INPUT_VALUE);
  const [loading, setLoading] = useState<boolean>(false);
  const { mutateAsync: asyncActivateBucket } = useActivateSavingBucket();
  const { refetch: moveMoney } = useMoveFromEmergencyBucketQuery({
    customerId,
    amount: currencyInputValueToNumber(inputValue),
    pullFromBucket,
    onSuccess: () => {
      if (pullFromBucket) {
        trackMixPanel('Withdrawal emergency bucket fund', {
          Origin: 'Emergency Jar Feed',
        });
      } else {
        trackMixPanel('Add emergency bucket fund', {
          Origin: 'Emergency Jar Feed',
          user_profile_type: 'smart',
        });
      }
      navigateWIthParams(pullFromBucket, inputValue);
    },
  });
  const isButtonDisabled = () => {
    if (inputValue === INITIAL_INPUT_VALUE || inputValue === NULL_INPUT_VALUE || inputValue === NULL_DOT_INPUT_VALUE) return true;
    if (summary) {
      if (pullFromBucket) {
        return currencyInputValueToNumber(inputValue) > summary.emergencyBucketResponse.balance;
      }
      return currencyInputValueToNumber(inputValue) > getPrimaryAccount().balance;
    }
    return true;
  };
  const showErrorMessage = () => {
    if (summary) {
      if (pullFromBucket) {
        return currencyInputValueToNumber(inputValue) > summary.emergencyBucketResponse.balance;
      }
      return currencyInputValueToNumber(inputValue) > getPrimaryAccount().balance;
    }
    return true;
  };

  const handleOnChange = (event: { target: { value: string } }) => {
    handleInputWithCurrencyOnChange(event, setInputValue);
  };
  const handleOnClick = async () => {
    if (!summary.emergencyBucketResponse.active) {
      await asyncActivateBucket();
    }
    moveMoney();
    setLoading(true);
  };

  const { t } = useTranslation();

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  });

  return (
    <div className='px-10 pt-[3.125rem] pb-[2.375em] bg-bw00 rounded-lg flex flex-col items-center'>
      <span className={TITLE.CLASS_NAME}>{pullFromBucket ? t(WITHDRAW.TITLE) : t(ADD.TITLE)}</span>
      <span className={`${TEXT.CLASS_NAME_STEP_1} flex flex-col`}>
        <span>
          <span className='text-action03 font-metricMedium text-lg leading-[1.313rem]'>
            {pullFromBucket ? `${EMERGENCY_BUCKET_BALANCE}` : `${CHECKING_BALANCE}`}
          </span>
          <span className=' leading-[1.313rem]'>
            {pullFromBucket
              ? t(
                validateFeatureFlag(FEATURE_FLAG.EMERGENCY_SAVING_ACCOUNT)
                  ? WITHDRAW.TEXT.PRO_1
                  : WITHDRAW.TEXT.BASIC_1,
              )
              : t(ADD.TEXT_1)}
          </span>
        </span>
        <span>
          {pullFromBucket
            ? t(
              validateFeatureFlag(FEATURE_FLAG.EMERGENCY_SAVING_ACCOUNT)
                ? WITHDRAW.TEXT.PRO_2
                : WITHDRAW.TEXT.BASIC_2,
            )
            : t(ADD.TEXT_2)}
        </span>
      </span>
      <InputWithCurrency
        inputRef={inputRef as React.Ref<HTMLInputElement>}
        containerClassName=''
        currency={CURRENCY_USD_SIGN}
        currencyClassName='font-metricMedium text-base text-bw07'
        id='EmergencyBucketMoneyMovement'
        inputCLassName={`${INPUT_CLASS_NAME} caret-action03 ${showErrorMessage() ? 'border-red02 mb-4' : 'border-action03 mb-[7.438rem]'}`}
        onChange={handleOnChange}
        value={inputValue}
        eventKey={ENTER_KEY}
        handleKeyDown={() => {
          if (!isButtonDisabled()) handleOnClick();
        }}
      />
      {showErrorMessage() && (
        <span className={ERROR_MESSAGE_CLASS_NAME}>
          {t(
            pullFromBucket
              ? validateFeatureFlag(FEATURE_FLAG.EMERGENCY_SAVING_ACCOUNT)
                ? WITHDRAW.ERROR_MESSAGE_PRO
                : WITHDRAW.ERROR_MESSAGE_BASIC
              : ADD.ERROR_MESSAGE,
          )}
        </span>
      )}
      <LiliButton
        id='moneyMoveSetAmount'
        onClick={handleOnClick}
        disabled={isButtonDisabled()}
        className='w-[17.5rem] h-[3.125rem]'
        isLoading={loading}
        title={t(pullFromBucket ? WITHDRAW.BUTTON : ADD.BUTTON)}
      />
    </div>
  );
};

export default EmergencyBucketMoneyMovement;
